import { useEffect, useState } from "react"

import { toast } from "sonner"

import type { DurableChatTurnIdentity, ToolApproval } from "@/api/models"

import { Button } from "@/components/ui/button"
import { useToolApprovalActions } from "@/features/chat/hooks/use-tool-approval-actions"
import { useToolApprovalKeyboard } from "@/features/chat/hooks/use-tool-approval-keyboard"
import { logger } from "@/lib/logger"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"

import { ToolApprovalCard } from "./tool-approval-card"
import { ToolApprovalHistory } from "./tool-approval-history"

type ToolApprovalOverlayProps = {
  approvals: ToolApproval[]
  toolApprovalSessionId: string | null
  activeSessionId?: string | null
  sidebarExpanded?: boolean
  /**
   * The active session's in-flight durable turn, when there is one (issue
   * #2513 findings 7/8). `approvals`/`toolApprovalSessionId` are populated
   * for a durable turn's `waiting-approval` status exactly like a legacy WS
   * one (`mapDurableChatTurnStateToSessionPatch` in durable-chat-turn-state.ts),
   * so without this the overlay would render the cards but route every
   * decision through the legacy WebSocket lookup — which has no socket to
   * find for a durable-only turn and fails outright. Presence alone decides
   * the transport; a session with no durable turn attached (`null`) keeps the
   * exact legacy behavior.
   */
  durableTurn?: DurableChatTurnIdentity | null
}

export function ToolApprovalOverlay({
  approvals,
  toolApprovalSessionId,
  activeSessionId,
  sidebarExpanded = false,
  durableTurn = null,
}: ToolApprovalOverlayProps) {
  const primarySessionId = toolApprovalSessionId ?? activeSessionId ?? null
  const fallbackSessionId = activeSessionId ?? null
  const hasSessionContext = Boolean(primarySessionId)
  const durable = durableTurn
    ? { durableTurn, getToken: () => SettingsService.getInstance().token }
    : null

  const [isBatchProcessing, setIsBatchProcessing] = useState(false)
  const { approve, deny, approveAll, denyAll } = useToolApprovalActions(
    primarySessionId,
    fallbackSessionId,
    durable
  )

  // Only show pending approvals in the overlay
  const pendingApprovals = approvals.filter((approval) => approval.status === "pending")

  const handleApproveAll = async () => {
    // Never bulk-approve a request whose arguments were dropped to fit the
    // durable event's size budget (issue #2513, Security Reviewer) -- it
    // still executes with its full, original arguments after approval, so
    // approving it blind defeats the individual card's own Approve gate.
    const approvable = pendingApprovals.filter((approval) => !approval.argumentsElided)
    const skippedCount = pendingApprovals.length - approvable.length
    if (approvable.length === 0) {
      if (skippedCount > 0) {
        toast.error("Cannot approve", {
          description:
            "Every pending approval has hidden arguments and must be reviewed individually.",
        })
      }
      return
    }
    setIsBatchProcessing(true)
    try {
      await approveAll(
        approvable.map((approval) => ({
          request_id: approval.request_id,
          call_id: approval.call_id,
        }))
      )
      if (skippedCount > 0) {
        toast.warning(`Approved ${approvable.length} of ${pendingApprovals.length}`, {
          description: `${skippedCount} approval${skippedCount === 1 ? "" : "s"} with hidden arguments must be reviewed individually.`,
        })
      }
    } catch (error) {
      toast.error("Error", {
        description: error instanceof Error ? error.message : String(error),
      })
    } finally {
      setIsBatchProcessing(false)
    }
  }

  const handleDenyAll = async () => {
    if (
      !window.confirm(
        `Are you sure you want to deny all ${pendingApprovals.length} pending approvals?`
      )
    ) {
      return
    }
    setIsBatchProcessing(true)
    try {
      await denyAll(
        pendingApprovals.map((approval) => ({
          request_id: approval.request_id,
          call_id: approval.call_id,
        })),
        "Batch denied by user"
      )
    } catch (error) {
      toast.error("Error", {
        description: error instanceof Error ? error.message : String(error),
      })
    } finally {
      setIsBatchProcessing(false)
    }
  }

  // Enable keyboard shortcuts when overlay is visible
  useToolApprovalKeyboard({
    pendingApprovals,
    onApprove: approve,
    onDeny: deny,
    onApproveAll: handleApproveAll,
    onDenyAll: handleDenyAll,
    enabled: pendingApprovals.length > 0 && !isBatchProcessing,
  })

  useEffect(() => {
    if (pendingApprovals.length === 0 || !hasSessionContext) {
      return
    }
    logger.info("ToolApprovalOverlay pending approvals", {
      toolApprovalSessionId,
      activeSessionId,
      primarySessionId,
      requestIds: pendingApprovals.map((approval) => approval.request_id),
      totalCount: pendingApprovals.length,
    })
  }, [
    activeSessionId,
    hasSessionContext,
    pendingApprovals,
    primarySessionId,
    toolApprovalSessionId,
  ])

  if (pendingApprovals.length === 0 || !hasSessionContext) {
    return null
  }

  const hasCompletedApprovals = approvals.some(
    (approval) => approval.status !== "pending" && approval.status !== "cancelled"
  )

  return (
    <section
      className={cn(
        "pointer-events-none fixed bottom-4 z-40 flex flex-col gap-3 transition-[right] duration-200",
        // Mobile: centered with padding on both sides
        "right-4 left-4 mx-auto max-w-md",
        // Desktop: positioned from right edge, accounting for sidebar
        "sm:left-auto sm:mx-0 sm:w-full",
        sidebarExpanded ? "sm:right-[calc(17rem+1rem)]" : "sm:right-4"
      )}
    >
      {pendingApprovals.length > 1 && (
        <div className="bg-muted/90 pointer-events-auto flex flex-col gap-2 rounded-md p-3 shadow-lg backdrop-blur-sm">
          <div className="text-muted-foreground text-center text-sm font-medium">
            {pendingApprovals.length} approvals pending
          </div>
          <div className="flex gap-2">
            <Button
              className="flex-1"
              disabled={isBatchProcessing}
              size="sm"
              variant="default"
              onClick={() => void handleApproveAll()}
            >
              Approve All
            </Button>
            <Button
              className="flex-1"
              disabled={isBatchProcessing}
              size="sm"
              variant="secondary"
              onClick={() => void handleDenyAll()}
            >
              Deny All
            </Button>
          </div>
          <div className="text-muted-foreground/80 text-center text-xs">
            Shortcuts: Enter/A = Approve • D = Deny • 1-9 = Quick select
          </div>
        </div>
      )}
      {pendingApprovals.map((approval, index) => (
        <ToolApprovalCard
          key={approval.request_id}
          approval={approval}
          className="pointer-events-auto shadow-xl"
          disabled={isBatchProcessing}
          durable={durable}
          fallbackSessionId={fallbackSessionId}
          position={index + 1}
          sessionId={primarySessionId}
          showPosition={pendingApprovals.length > 1}
          showShortcutsHint={pendingApprovals.length === 1}
          total={pendingApprovals.length}
        />
      ))}
      {hasCompletedApprovals && (
        <ToolApprovalHistory approvals={approvals} className="pointer-events-auto shadow-lg" />
      )}
    </section>
  )
}
